<template>
  <div v-if="showBanner" class="trial-banner" :class="urgencyClass">
    <div class="trial-banner__inner">
      <span class="trial-banner__icon">{{ urgencyIcon }}</span>
      <div class="trial-banner__text">
        <strong>{{ headlineText }}</strong>
        <span>{{ subText }}</span>
      </div>
      <button
        v-if="canExtendTrial"
        class="trial-banner__extend"
        :disabled="extending"
        @click="extendTrial"
      >{{ extending ? 'Extending…' : 'Extend 7 Days (Free)' }}</button>
      <button class="trial-banner__cta" @click="handleUpgrade">Upgrade Now</button>
      <button class="trial-banner__dismiss" @click="dismiss" title="Dismiss">✕</button>
    </div>
  </div>
</template>

<script>
import { api } from '@/services/api'

export default {
  name: 'TrialBanner',
  data() {
    return {
      dismissed:             false,
      extending:             false,
      subscriptionStatus:    null,
      trialEndsAt:           null,
      trialExtensionCount:   0,
    }
  },
  computed: {
    daysRemaining() {
      if (!this.trialEndsAt) return null
      const end   = new Date(this.trialEndsAt)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return Math.max(0, Math.ceil((end - today) / 86400000))
    },
    showBanner() {
      if (this.dismissed) return false
      if (this.subscriptionStatus !== 'TRIAL') return false
      if (this.daysRemaining === null) return false
      return this.daysRemaining <= 14
    },
    canExtendTrial() {
      return this.trialExtensionCount === 0 && this.daysRemaining !== null && this.daysRemaining <= 7
    },
    urgencyClass() {
      if (this.daysRemaining <= 3) return 'trial-banner--urgent'
      if (this.daysRemaining <= 7) return 'trial-banner--warning'
      return 'trial-banner--normal'
    },
    urgencyIcon() {
      if (this.daysRemaining <= 3) return '⚠️'
      return '⏰'
    },
    headlineText() {
      if (this.daysRemaining === 0) return 'Your trial expires today!'
      if (this.daysRemaining === 1) return '1 day left in your trial'
      return `${this.daysRemaining} days left in your trial`
    },
    subText() {
      if (this.daysRemaining <= 3) return 'Upgrade now to avoid any service interruption.'
      return 'Upgrade to a paid plan to keep full access after your trial ends.'
    },
  },
  mounted() {
    this.subscriptionStatus  = localStorage.getItem('SUBSCRIPTION_STATUS') || null
    this.trialEndsAt         = localStorage.getItem('TRIAL_ENDS_AT')       || null
    this.trialExtensionCount = parseInt(localStorage.getItem('TRIAL_EXTENSION_COUNT') || '0', 10)
    if (sessionStorage.getItem('trial_banner_dismissed') === '1') {
      this.dismissed = true
    }
  },
  methods: {
    handleUpgrade() {
      // Route by name, not a hardcoded path string — the registered path is
      // "/payment-required" (lowercase), not "/PaymentRequired"; pushing the
      // wrong-case path silently matches nothing and leaves a blank page.
      this.$router.push({ name: 'PaymentRequired' })
    },
    async extendTrial() {
      this.extending = true
      try {
        const res = await api.post('subscriptions/extend_trial')
        const data = res.data
        if (data.success) {
          this.trialEndsAt         = data.trial_ends_at
          this.trialExtensionCount = data.trial_extension_count
          localStorage.setItem('TRIAL_ENDS_AT',          data.trial_ends_at)
          localStorage.setItem('TRIAL_EXTENSION_COUNT',  data.trial_extension_count)
          // Clear any payment block — user now has more time
          localStorage.removeItem('PAYMENT_BLOCKED')
        } else {
          alert(data.message || 'Could not extend trial. Please try upgrading.')
        }
      } catch (err) {
        alert(err?.response?.data?.message || 'Could not extend trial.')
      } finally {
        this.extending = false
      }
    },
    dismiss() {
      this.dismissed = true
      sessionStorage.setItem('trial_banner_dismissed', '1')
    },
  },
}
</script>

<style scoped>
.trial-banner {
  width: 100%;
  z-index: 50;
}
.trial-banner__inner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  font-size: 13px;
}
.trial-banner--normal  .trial-banner__inner { background: #EFF6FF; border-bottom: 2px solid #3B82F6; }
.trial-banner--warning .trial-banner__inner { background: #FFFBEB; border-bottom: 2px solid #F59E0B; }
.trial-banner--urgent  .trial-banner__inner { background: #FEF2F2; border-bottom: 2px solid #EF4444; }

.trial-banner__icon { font-size: 16px; flex-shrink: 0; }
.trial-banner__text { display: flex; gap: 6px; align-items: center; flex: 1; flex-wrap: wrap; }
.trial-banner__text strong { font-weight: 700; color: #1E1E2F; white-space: nowrap; }
.trial-banner__text span   { color: #6B7280; }

.trial-banner__extend {
  flex-shrink: 0;
  padding: 5px 14px;
  border-radius: 6px;
  background: transparent;
  color: #3B4CC0;
  font-size: 12px;
  font-weight: 700;
  border: 1.5px solid #3B4CC0;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity .15s;
}
.trial-banner--warning .trial-banner__extend { color: #D97706; border-color: #D97706; }
.trial-banner--urgent  .trial-banner__extend { color: #DC2626; border-color: #DC2626; }
.trial-banner__extend:hover { opacity: .80; }
.trial-banner__extend:disabled { opacity: .55; cursor: not-allowed; }

.trial-banner__cta {
  flex-shrink: 0;
  padding: 5px 14px;
  border-radius: 6px;
  background: #3B4CC0;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity .15s;
}
.trial-banner--warning .trial-banner__cta { background: #D97706; }
.trial-banner--urgent  .trial-banner__cta { background: #DC2626; }
.trial-banner__cta:hover { opacity: .88; }

.trial-banner__dismiss {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #9CA3AF;
  font-size: 14px;
  padding: 2px 4px;
  line-height: 1;
}
.trial-banner__dismiss:hover { color: #374151; }
</style>
